import { Options as PopperOptions } from '@popperjs/core/lib/types';
import cn from 'classnames';
import * as React from 'react';
import { usePopper } from 'react-popper';
import { CSSTransition } from 'react-transition-group';
import { TransitionProps } from 'react-transition-group/Transition';

import { useClickOutside } from 'hooks/useClickOutside';
import { preventBubbling } from 'utils/preventBubbling';

import Portal from '../Portal';

import s from './PopperPanel.module.scss';

export type PopperPanelOptions = Partial<PopperOptions>;

export type PopperPanelProps = React.PropsWithChildren<{
  referenceElement: HTMLElement | null;
  className?: string;
  open: boolean;
  options?: PopperPanelOptions;
  popperClassName?: string;
  portalClassName?: string;
  arrowClassName?: string;
  sameWidth?: boolean;
  classNameAnimation?: string;
  onMouseOver?: VoidFunction;
  onMouseLeave?: VoidFunction;
  withShadow?: boolean;
  close?: VoidFunction;
  ignoreClass?: string;
  ignoreId?: string;
  onEnter?: TransitionProps['onEnter'];
  onExited?: TransitionProps['onExited'];
  withArrow?: boolean;
  getUpdate?: React.MutableRefObject<VoidFunction | null>;
}>;

const PopperPanel: React.FC<PopperPanelProps> = ({
  referenceElement,
  children,
  className,
  open,
  options,
  popperClassName,
  arrowClassName,
  sameWidth = false,
  classNameAnimation = 'animation-fade',
  onMouseOver,
  onMouseLeave,
  withShadow = false,
  close,
  ignoreClass,
  ignoreId,
  onEnter,
  onExited,
  withArrow,
  getUpdate,
}) => {
  const [popper, setPopper] = React.useState<HTMLDivElement | null>(null);
  const [arrowElement, setArrowElement] = React.useState<HTMLDivElement | null>(null);
  const popperRef = React.useRef<HTMLDivElement>();

  const { styles, attributes, update } = usePopper(referenceElement, popper, {
    placement: 'bottom-start',
    strategy: 'fixed',
    ...options,
    modifiers: withArrow
      ? [{ name: 'arrow', options: { element: arrowElement } }, ...(options?.modifiers || [])]
      : options?.modifiers,
  });

  React.useEffect(() => {
    const orientationChangeHandler = () => update && update();

    addEventListener('orientationchange', orientationChangeHandler);

    if (getUpdate && update) {
      getUpdate.current = update;
    }

    return () => removeEventListener('orientationchange', orientationChangeHandler);
  }, [getUpdate, update]);

  React.useEffect(() => {
    if (!popper) {
      return;
    }

    popperRef.current = popper;
  }, [popper, popperRef]);

  useClickOutside({ ref: popperRef, handler: close, ignoreClass, ignoreId });

  return (
    <Portal>
      <CSSTransition
        unmountOnExit
        in={open}
        timeout={300}
        classNames={classNameAnimation}
        onEnter={onEnter}
        onExited={onExited}
      >
        <div
          onMouseDown={preventBubbling}
          onMouseOver={onMouseOver}
          onMouseLeave={onMouseLeave}
          className={cn(className, s.popper, withShadow && s['popper_with-shadow'])}
          ref={setPopper}
          style={{
            ...styles.popper,
            width: sameWidth ? referenceElement?.offsetWidth : undefined,
          }}
          {...attributes.popper}
        >
          <div className={cn(s.popper__panel, popperClassName)}>
            {children}
            {withArrow && (
              <div ref={setArrowElement} className={cn(arrowClassName, s.popper__arrow)} style={styles.arrow} />
            )}
          </div>
        </div>
      </CSSTransition>
    </Portal>
  );
};

export default PopperPanel;
